import { Plus } from 'lucide-react';
import React from 'react';
import { useTeamTab } from '../../TeamTabContext';

export const AddTeamMemberPanel: React.FC = () => {
  const {
    teamAddMemberSectionRef,
    highlightTeamAddMemberSection,
    newMember,
    setNewMember,
    availableRoles,
    setAvailableRoles,
    showAddRoleInput,
    setShowAddRoleInput,
    setShowManageRoles,
    newRoleName,
    setNewRoleName,
    newRoleInputRef,
    addMemberRosterPick,
    setAddMemberRosterPick,
    addMemberSearchResults,
    setAddMemberSearchResults,
    addMemberSearchLoading,
    handleAddTeamMember,
    profileApiFetch,
    loadRoles,
  } = useTeamTab();

  return (
    <div
      ref={teamAddMemberSectionRef as React.Ref<HTMLDivElement>}
      id="profile-team-add-member"
      className={
        highlightTeamAddMemberSection ? 'profile-team-add-member-highlight-section' : undefined
      }
      style={{
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'var(--profile-nested-bg)',
        borderRadius: '12px',
        border: '1px solid var(--profile-nested-border)',
      }}
    >
      <h3
        style={{
          marginBottom: '16px',
          fontSize: '15px',
          fontWeight: '600',
          color: 'var(--color-text)',
        }}
      >
        Add Team Member
      </h3>
      <p
        style={{
          marginBottom: '16px',
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.45,
        }}
      >
        Adds a person to the <strong>team roster</strong> for the <strong>current product</strong>{' '}
        (header selector). Same flow as suggestions. Does not create a Relix login.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(80px, 0.8fr) 80px',
          gap: '12px',
          alignItems: 'end',
          marginBottom: '12px',
        }}
      >
        <div style={{ position: 'relative' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Role
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select
              id="profile-team-add-member-role-select"
              className={`form-input${
                highlightTeamAddMemberSection ? ' profile-team-add-member-highlight-control' : ''
              }`}
              value={newMember.role || 'developer'}
              onChange={(e) => {
                if (e.target.value === '__add_new__') {
                  setShowAddRoleInput(true);
                } else {
                  setNewMember({ ...newMember, role: e.target.value });
                }
              }}
              style={{ width: '100%', paddingRight: '28px', minHeight: '38px' }}
            >
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role.toLowerCase() === 'qa'
                    ? 'QA'
                    : role.toLowerCase() === 'developer' || role.toLowerCase() === 'dev'
                      ? 'Dev'
                      : role}
                </option>
              ))}
              <option value="__add_new__">+ Add New Role</option>
            </select>
            <button
              type="button"
              id="profile-team-add-member-new-role-btn"
              className={
                highlightTeamAddMemberSection
                  ? 'profile-team-add-member-highlight-control'
                  : undefined
              }
              onClick={() => setShowAddRoleInput(true)}
              title="Add new role"
              style={{
                width: '32px',
                height: '38px',
                flexShrink: 0,
                border: '1px solid #6366f1',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: '#6366f1',
                fontSize: '18px',
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              +
            </button>
            <button
              type="button"
              onClick={() => setShowManageRoles((v) => !v)}
              title="Manage roles"
              style={{
                padding: '6px 10px',
                fontSize: '11px',
                color: '#6366f1',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              }}
            >
              Manage
            </button>
          </div>
          {showAddRoleInput && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                zIndex: 10,
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                backgroundColor: 'var(--profile-nested-table-row-bg)',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--profile-nested-border)',
                boxShadow: 'var(--shadow-md)',
                minWidth: '320px',
                width: 'max-content',
                maxWidth: '100%',
              }}
            >
              <input
                ref={newRoleInputRef as React.Ref<HTMLInputElement>}
                type="text"
                className="form-input preserve-case-input"
                placeholder="New role name (e.g. BE Developer)"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const raw = newRoleInputRef.current?.value?.trim() ?? newRoleName.trim();
                    if (!raw) return;
                    const roleName = raw;
                    const isDuplicate = availableRoles.some(
                      (r) => r.toLowerCase() === roleName.toLowerCase()
                    );
                    if (!isDuplicate) {
                      profileApiFetch('/api/roles', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: roleName }),
                      })
                        .then((r) => {
                          if (r.ok) loadRoles();
                        })
                        .catch(() => {});
                      setAvailableRoles([...availableRoles, roleName].sort());
                      setNewMember({ ...newMember, role: roleName });
                    }
                    setShowAddRoleInput(false);
                    setNewRoleName('');
                  }
                }}
                style={{
                  flex: 1,
                  minWidth: '180px',
                  backgroundColor: 'var(--color-bg-input)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-border-strong)',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  textTransform: 'none',
                }}
                autoComplete="off"
                autoCapitalize="words"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
              />
              <button
                onClick={() => {
                  const raw = newRoleInputRef.current?.value?.trim() ?? newRoleName.trim();
                  if (!raw) return;
                  const roleName = raw;
                  const isDuplicate = availableRoles.some(
                    (r) => r.toLowerCase() === roleName.toLowerCase()
                  );
                  if (!isDuplicate) {
                    profileApiFetch('/api/roles', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name: roleName }),
                    })
                      .then((r) => {
                        if (r.ok) loadRoles();
                      })
                      .catch(() => {});
                    setAvailableRoles([...availableRoles, roleName].sort());
                    setNewMember({ ...newMember, role: roleName });
                  }
                  setShowAddRoleInput(false);
                  setNewRoleName('');
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#6366f1',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddRoleInput(false);
                  setNewRoleName('');
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            className="form-input"
            placeholder="Search roster or type a new name"
            value={newMember.name || ''}
            onChange={(e) => {
              setAddMemberRosterPick(null);
              setNewMember({ ...newMember, name: e.target.value });
            }}
            autoComplete="off"
            style={{ width: '100%', minHeight: '38px' }}
          />
          {(newMember.name || '').trim().length >= 2 && addMemberSearchLoading ? (
            <p
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '100%',
                margin: '4px 0 0',
                fontSize: '11px',
                color: 'var(--color-text-secondary)',
              }}
            >
              Searching roster\u2026
            </p>
          ) : null}
          {(newMember.name || '').trim().length >= 2 &&
          !addMemberSearchLoading &&
          addMemberSearchResults.length > 0 &&
          !addMemberRosterPick ? (
            <ul
              role="listbox"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '100%',
                margin: '4px 0 0',
                padding: '4px 0',
                maxHeight: 200,
                overflowY: 'auto',
                backgroundColor: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 8,
                boxShadow: 'var(--shadow-md)',
                zIndex: 30,
                listStyle: 'none',
              }}
            >
              {addMemberSearchResults.map((r) => {
                const rid = r.id != null ? String(r.id) : '';
                const label = String(r.display_name || '').trim();
                return (
                  <li key={rid || label}>
                    <button
                      type="button"
                      className="form-input"
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        borderRadius: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 13,
                        padding: '8px 12px',
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setAddMemberRosterPick(r);
                        setNewMember({
                          ...newMember,
                          name: label,
                          email:
                            typeof r.email === 'string' && r.email.trim() !== ''
                              ? r.email.trim()
                              : '',
                          jiraUsername:
                            typeof r.jira_username === 'string' && r.jira_username.trim() !== ''
                              ? r.jira_username.trim()
                              : '',
                        });
                        setAddMemberSearchResults([]);
                      }}
                    >
                      {label || '\u2014'}{' '}
                      {typeof r.email === 'string' && r.email.trim() !== '' ? (
                        <span
                          style={{
                            color: 'var(--color-text-muted)',
                            fontSize: 12,
                          }}
                        >
                          ({r.email.trim()})
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {(newMember.name || '').trim().length >= 2 &&
          !addMemberSearchLoading &&
          addMemberSearchResults.length === 0 &&
          !addMemberRosterPick ? (
            <p
              style={{
                marginTop: 6,
                fontSize: '11px',
                color: 'var(--color-text-muted)',
              }}
            >
              No roster match — fills as <strong>Create new</strong> when you add.
            </p>
          ) : null}
          {addMemberRosterPick ? (
            <p
              style={{
                marginTop: 6,
                fontSize: '11px',
                color: '#059669',
              }}
            >
              Using existing roster person — will add the current product if needed.
            </p>
          ) : null}
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Email
          </label>
          <input
            type="email"
            className="form-input"
            placeholder="email@example.com"
            value={newMember.email || ''}
            onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
            style={{ width: '100%', minHeight: '38px' }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Jira Username
          </label>
          <input
            type="text"
            className="form-input"
            placeholder="Jira username"
            value={newMember.jiraUsername || ''}
            onChange={(e) => setNewMember({ ...newMember, jiraUsername: e.target.value })}
            style={{ width: '100%', minHeight: '38px' }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Hours/Day
          </label>
          <input
            type="number"
            className="form-input"
            min="0"
            max="24"
            step="0.5"
            value={newMember.capacityHoursPerDay || 8.0}
            onChange={(e) =>
              setNewMember({
                ...newMember,
                capacityHoursPerDay: parseFloat(e.target.value) || 8.0,
              })
            }
            style={{ width: '100%', minHeight: '38px' }}
          />
        </div>
        <div />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto minmax(120px, 1fr) minmax(120px, 1fr) 1fr',
          gap: '12px',
          alignItems: 'center',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={newMember.includeInCapacity !== false}
            onChange={(e) => setNewMember({ ...newMember, includeInCapacity: e.target.checked })}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Include in capacity calculation"
          />
          Capacity
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <input
            type="checkbox"
            checked={newMember.includeInHistoricalCapacity === true}
            onChange={(e) =>
              setNewMember({
                ...newMember,
                includeInHistoricalCapacity: e.target.checked,
              })
            }
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Include in historical capacity (past releases)"
          />
          Historical
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <input
            type="checkbox"
            checked={newMember.excludeFromDistribution === true}
            onChange={(e) =>
              setNewMember({
                ...newMember,
                excludeFromDistribution: e.target.checked,
              })
            }
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Exclude from forecast distribution (Suggest distribution)"
          />
          Excl. from Forecast
        </label>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            Start date in team
          </label>
          <input
            type="date"
            className="form-input"
            value={newMember.startDateInTeam || ''}
            onChange={(e) =>
              setNewMember({
                ...newMember,
                startDateInTeam: e.target.value || undefined,
              })
            }
            style={{ width: '100%', minHeight: '38px' }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--color-text-secondary)',
            }}
          >
            End date in team
          </label>
          <input
            type="date"
            className="form-input"
            value={newMember.endDateInTeam || ''}
            onChange={(e) =>
              setNewMember({
                ...newMember,
                endDateInTeam: e.target.value || undefined,
              })
            }
            style={{ width: '100%', minHeight: '38px' }}
            title="Leave empty if still in team"
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            id="profile-team-add-member-submit-btn"
            className={
              highlightTeamAddMemberSection
                ? 'profile-team-add-member-highlight-control'
                : undefined
            }
            onClick={handleAddTeamMember}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#6366f1',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minHeight: '38px',
            }}
          >
            <Plus size={18} />
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
